import React, { useEffect, useState, useRef } from "react"
import { ClipboardDocumentIcon } from "@heroicons/react/24/outline"
import { showNotification } from "aldoo-ra/Notification"
import { showPromptDialog } from "aldoo-ra/PromptDialog"
import ToggleSwitch from "aldoo-ra/ToggleSwitch"
import { useRealtime } from "aldoo-ra/Realtime/hooks"
import { GetStats } from "../api"

/**
 * Subscribe to the ads realtime namespace and call `onPing` (debounced) on
 * every KPI event (impression/click), so dashboards refetch live. Only the
 * origin/local-account dashboard receives these — remote accounts emit on
 * their own server's channel. Pass a stable `onPing` (useCallback).
 */
export const useAdsKpiPing = (onPing, delay = 2500) => {
  const { socket } = useRealtime("ads")
  const timer = useRef(null)
  useEffect(() => {
    if (!socket || !onPing) return
    const handler = () => {
      clearTimeout(timer.current)
      timer.current = setTimeout(onPing, delay)
    }
    socket.on("ads:kpi", handler)
    return () => { socket.off("ads:kpi", handler); clearTimeout(timer.current) }
  }, [socket, onPing, delay])
}

/** Renderer for clickable name cells — styled as a primary link so the
 *  row's drill-down affordance is obvious in the DataTable. */
export const linkCell = (v) => (
  <span className="text-admin_primary font-medium hover:underline cursor-pointer">{v}</span>
)

/** Name cell with a serving toggle (active/stopped). The toggle stops row-
 *  click propagation so flipping it doesn't drill into the row. `onToggle`
 *  receives the new boolean. Read-only rows (remote) omit the toggle. */
export const NameToggleCell = ({ name, isOn, onToggle }) => (
  <div className="flex items-center gap-3">
    {onToggle && (
      <span onClick={(e) => e.stopPropagation()} className="shrink-0">
        <ToggleSwitch variant="admin" isOn={isOn} onChange={(v) => onToggle(v)} />
      </span>
    )}
    <span className="text-admin_primary font-medium hover:underline cursor-pointer">{name}</span>
  </div>
)

/** Standard Aldoo-RA confirm dialog before a soft-delete. */
export const confirmDelete = (label, onConfirm) =>
  showPromptDialog({
    variant: "admin",
    title: `Delete ${label}?`,
    message: `This removes "${label}" and everything under it. This cannot be undone.`,
    confirmText: "Delete",
    cancelText: "Cancel",
    onConfirm,
  })

/** Copy-to-clipboard chip for ad ids — the only thing an integration needs:
 *  <AldooAd id="..."/> (the server resolves which account serves it). */
export const CopyId = ({ id }) => (
  <button
    className="inline-flex items-center gap-1 text-xs font-mono text-admin_text_secondary hover:text-admin_text"
    onClick={(e) => {
      e.stopPropagation()
      navigator.clipboard?.writeText(id)
      showNotification({ message: "Ad id copied", className: "bg-admin_success text-admin_text_inv" })
    }}
    title={`Copy id ${id}`}
  >
    <ClipboardDocumentIcon className="w-4 h-4" />
    {String(id).slice(-6)}
  </button>
)

/** DateFilterDropdown value → { from, to } ISO range for the stats action. */
export const rangeFromFilter = (value) => {
  if (!value) return {}
  if (value.type === "custom" && value.range) {
    return { from: value.range.startDate, to: value.range.endDate }
  }
  if (value.type === "preset" && value.value?.days) {
    const from = new Date(Date.now() - value.value.days * 24 * 60 * 60 * 1000)
    return { from: from.toISOString() }
  }
  return {} // lifetime
}

export const formatRevenue = (rev) => {
  if (!Array.isArray(rev) || !rev.length) return "$0"
  return rev
    .map((r) => `${r.currency === "usd" ? "$" : r.currency.toUpperCase() + " "}${r.amount}`)
    .join(" · ")
}

/** DataTable sort ({field, order}) → mongo sort object, with a fallback. */
export const sortArg = (s, fallback = { created: -1 }) =>
  s?.field ? { [s.field]: s.order === "desc" ? -1 : 1 } : fallback

/** Sort an in-memory row array by a DataTable sort ({field, order}). */
export const applySort = (rows, s) => {
  if (!s?.field) return rows
  const dir = s.order === "desc" ? -1 : 1
  return [...rows].sort((a, b) => {
    const av = a?.[s.field], bv = b?.[s.field]
    if (av == null && bv == null) return 0
    if (av == null) return 1
    if (bv == null) return -1
    return av < bv ? -dir : av > bv ? dir : 0
  })
}

const sumRev = (arr) => (Array.isArray(arr) ? arr.reduce((s, r) => s + (r.amount || 0), 0) : 0)

/**
 * Attach numeric KPI fields to each row (from one batch stats call), then
 * sort + paginate the merged set so EVERY column — real DB fields and the
 * computed KPIs — sorts through the same path. KPIs can't be server-sorted,
 * so the loader fetches all rows + their stats, sorts in memory, and returns
 * the requested page. Datasets here are small (campaigns/ads per account).
 *
 * Each row gains numeric `_kpi_*` (the sortable values) plus `_stats` (the
 * raw stats object, for display formatting incl. the 'both' touch). For the
 * 'both' touch, the last-touch values are used as the sort key.
 */
export const withKPIRows = async ({ rows, level, account, range, params }) => {
  const ids = rows.map((r) => r._id)
  let statsMap = {}
  if (ids.length) {
    const res = await GetStats({ level, ids, account, ...rangeFromFilter(range) }).catch(() => null)
    statsMap = res?.stats || {}
  }
  const merged = rows.map((r) => {
    const s = statsMap[r._id] || {}
    const both = s.touch === "both"
    return {
      ...r,
      _stats: s,
      _kpi_impressions: s.impressions ?? 0,
      _kpi_clicks: s.clicks ?? 0,
      _kpi_ctr: s.ctr ?? 0,
      _kpi_signups: both ? (s.last?.signups ?? 0) : (s.signups ?? 0),
      _kpi_revenue: both ? sumRev(s.last?.revenue) : sumRev(s.revenue),
    }
  })
  const sorted = applySort(merged, params.sort)
  const start = (params.page - 1) * params.itemsPerPage
  return { data: sorted.slice(start, start + params.itemsPerPage), total: sorted.length }
}

/** Signups / revenue display honoring the 'both' attribution touch. */
export const statValue = (stats, key) => {
  if (!stats) return "…"
  if (stats.touch === "both" && (key === "signups" || key === "revenue")) {
    const f = key === "revenue" ? formatRevenue(stats.first?.revenue) : stats.first?.signups ?? 0
    const l = key === "revenue" ? formatRevenue(stats.last?.revenue) : stats.last?.signups ?? 0
    return `${f} / ${l}`
  }
  if (key === "revenue") return formatRevenue(stats.revenue)
  if (key === "ctr") return `${stats.ctr ?? 0}%`
  return stats[key] ?? 0
}

const tileDefs = [
  { key: "impressions", label: "Impressions", color: "bg-blue-600" },
  { key: "clicks", label: "Clicks", color: "bg-red-600" },
  { key: "ctr", label: "CTR", color: "bg-amber-600" },
  { key: "signups", label: "Signups", color: "bg-emerald-600" },
  { key: "revenue", label: "Revenue", color: "bg-violet-600" },
]

/**
 * KPI tile row (Impressions / Clicks / CTR / Signups / Revenue) for one node
 * of the concept graph. Presentational — the date range picker lives in the
 * parent header (next to the action buttons) and feeds `range` in.
 */
const KPITiles = ({ level, id, account, range }) => {
  const [stats, setStats] = useState(null)

  useEffect(() => {
    if (!id) return
    let alive = true
    ;(async () => {
      const res = await GetStats({ level, id, account, ...rangeFromFilter(range) }).catch(() => null)
      if (alive) setStats(res?.result ? res : null)
    })()
    return () => { alive = false }
  }, [level, id, account, range])

  return (
    <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
      {tileDefs.map((def) => (
        <div key={def.key} className={`${def.color} text-white rounded-lg p-4`}>
          <div className="text-xs opacity-80">
            {def.label}
            {stats?.touch === "both" && (def.key === "signups" || def.key === "revenue")
              ? " (first / last)" : ""}
          </div>
          <div className="text-2xl font-bold mt-1">{stats ? statValue(stats, def.key) : "—"}</div>
        </div>
      ))}
    </div>
  )
}

export default KPITiles
